/**
 * @file This file contains the AnimalManager class, responsible for managing
 * animal spawning, movement, and interaction with the game grid.
 * @module AnimalManager
 */

export class AnimalManager {
    /**
     * @param {object} dependencies - The dependencies for the AnimalManager.
     * @param {import('../models/Grid.js').Grid} dependencies.grid - The game grid instance.
     * @param {import('./UIManager.js').UIManager} dependencies.ui - The UI manager instance.
     * @param {object} dependencies.gameConfig - The active game configuration.
     * @param {object} dependencies.textConfig - The text configuration for game messages.
     */
    constructor(dependencies) {
        this.grid = dependencies.grid;
        this.ui = dependencies.ui;
        this.gameConfig = dependencies.gameConfig;
        this.textConfig = dependencies.textConfig;

        /** @type {number} */
        this.turnsUntilSpawn = this.gameConfig.turnsPerAnimal;
        /** @type {object|null} */
        this.activeAnimal = null;
        /** @type {Map<number, {r: number, c: number}>} */
        this.animalsOnGrid = new Map();
        /** @type {number} */
        this.nextAnimalId = 0;
        /**
         * The number of animals currently within the playable grid area.
         * @type {number}
         */
        this.animalsOnGridCount = 0;
    }

    /**
     * Initializes or resets the AnimalManager for a new game session.
     */
    initManager() {
        this.turnsUntilSpawn = this.gameConfig.turnsPerAnimal;
        this.animalsOnGrid.clear();
        this.nextAnimalId = 0;
        this.animalsOnGridCount = 0;

        // Disable animals if the setting is off, OR if it's an easy campaign level.
        const disableAnimals = !this.gameConfig.isAnimalsEnabled || (this.gameConfig.isCampaignLevel && this.gameConfig.pathLength === 'simple' && this.gameConfig.gridSize <= 6);

        if (disableAnimals) {
            this.activeAnimal = null;
            this.turnsUntilSpawn = Infinity; // Effectively disable spawning
        } else {
            const animals = this.gameConfig.animals;
            if (animals && animals.length > 0) {
                this.activeAnimal = animals[Math.floor(Math.random() * animals.length)];
            } else {
                this.activeAnimal = null; // No animals configured
            }
        }
    }
    
    /**
     * Gets the number of animals currently on the playable grid.
     * @returns {number} The count of animals on the grid.
     */
    getAnimalCount() {
        return this.animalsOnGrid.size;
    }

    /**
     * Decrements counters for animal spawning and movement.
     */
    decrementCounters() {
        this.turnsUntilSpawn--;
        for (const pos of this.animalsOnGrid.values()) {
            const item = this.grid.get(pos.r, pos.c);
            if (item && item.turnsUntilMove !== undefined) {
                item.turnsUntilMove--;
            }
        }
    }

    /**
     * Handles the spawning of a new animal if the turnsUntilSpawn counter reaches zero.
     * @returns {{spawned: boolean, animalName?: string}} An object indicating if an animal was spawned and its name.
     */
    handleSpawning() {
        if (this.turnsUntilSpawn <= 1) {
            // Reset the counter. We add 1 because decrementCounters() is called
            // immediately after in the same turn, which would otherwise make the
            // cycle one turn shorter than intended.
            this.turnsUntilSpawn = this.gameConfig.turnsPerAnimal + 1;
            const spawnResult = this.spawn();
            if (spawnResult.spawned) {
                this.ui.updateGrid(this.grid.data, this.grid.size, this.gameConfig);
                this.ui.displayMessage(this.textConfig.gameMessages.animalAppeared.replace('{ANIMAL_NAME}', spawnResult.animalName));
            }
            return spawnResult.spawned;
        }
        return false;
    }

    /**
     * Spawns a new animal on a random empty cell on the grid.
     * @returns {{spawned: boolean, animalName?: string}} An object indicating if an animal was spawned and its animal name.
     */
    spawn() {
        if (!this.activeAnimal) {
            return { spawned: false };
        }

        const cell = this.grid.getRandomEmptyCell();
        if (cell) {
            const { r, c } = cell;
            const newAnimal = { ...this.activeAnimal, id: this.nextAnimalId++ };
            if (newAnimal.canMove) {
                newAnimal.turnsUntilMove = newAnimal.moveCooldownInTicks;
            }
            this.grid.set(r, c, newAnimal);
            this.animalsOnGrid.set(newAnimal.id, { r, c });
            return { spawned: true, animalName: this.activeAnimal.name };
        }
        return { spawned: false };
    }

    /**
     * Gets the animal item at a specific grid position.
     * @param {number} r - The row index.
     * @param {number} c - The column index.
     * @returns {object|null} The animal item if found, otherwise null.
     */
    getAnimalAt(r, c) {
        const item = this.grid.get(r, c);
        if (item && item.id !== undefined && this.animalsOnGrid.has(item.id)) {
            return item;
        }
        return null;
    }

    /**
     * Removes an animal from the animalsOnGrid map at a specific position.
     * @param {number} r - The row index.
     * @param {number} c - The column index.
     * @returns {boolean} True if an animal was removed, false otherwise.
     */
    removeAnimalAt(r, c) {
        const item = this.grid.get(r, c);
        if (item && item.id !== undefined && this.animalsOnGrid.has(item.id)) {
            return this.animalsOnGrid.delete(item.id);
        }
        return false;
    }

    /**
     * Handles the movement of animals that are ready to move.
     * @param {function(boolean): void} onComplete - Callback function to execute after movement,
     *                                               receives true if any animals moved, false otherwise.
     */
    handleMovement(onComplete) {
        const animalsReadyToMove = this._getAnimalsReadyToMove();
        const movesToAnimate = this._calculateMoves(animalsReadyToMove);

        if (movesToAnimate.length === 0) {
            onComplete(false);
            return;
        }

        this._applyMoves(movesToAnimate);

        this.ui.updateGrid(this.grid.data, this.grid.size, this.gameConfig);
        this.ui.displayMessage(this.textConfig.gameMessages.animalsMoved);

        onComplete(true);
    }

    /**
     * Checks for animals surrounded by items and "traps" them, adding to the score.
     * @returns {{newlyTrappedItems: Array<{r: number, c: number}>, score: number}} An object containing
     *          the positions of newly created trapped items and the score gained.
     */
    checkAndTrapAnimals() {
        const trappedAnimalData = this._findTrappedAnimals();

        if (trappedAnimalData.length === 0) {
            return { newlyTrappedItems: [], score: 0 };
        }

        const newlyTrappedItems = [];
        let lastTrappedAnimalName = '';
        let score = 0;
        trappedAnimalData.forEach(animalData => {
            if(animalData.item){
                this.grid.set(animalData.r, animalData.c, this.gameConfig.genericUpgradePath[0]);
                score += 50; // Game.TRAP_SCORE;
                newlyTrappedItems.push({ r: animalData.r, c: animalData.c });

                lastTrappedAnimalName = animalData.item.name;
            }
            this.animalsOnGrid.delete(animalData.id);
        });

        if (newlyTrappedItems.length > 0) {
            const animalName = lastTrappedAnimalName || 'animal';
            this.ui.displayMessage(this.textConfig.gameMessages.cratedAnimal.replace('{ANIMAL_NAME}', animalName));
        }
        return { newlyTrappedItems, score };
    }

    /**
     * Finds all animals that are surrounded and can be trapped.
     * @returns {Array<object>} A list of animals to be trapped.
     * @private
     */
    _findTrappedAnimals() {
        const trappedAnimalData = [];
        for (const [id, pos] of this.animalsOnGrid.entries()) {
            const { r, c } = pos;
            const neighbors = [
                this.grid.get(r - 1, c), this.grid.get(r + 1, c),
                this.grid.get(r, c - 1), this.grid.get(r, c + 1)
            ];
            if (neighbors.every(n => n !== null && n !== undefined)) {
                const animalItem = this.grid.get(r, c);
                trappedAnimalData.push({ r, c, item: animalItem, id: id });
            }
        }
        return trappedAnimalData;
    }

    /**
     * Gets a shuffled list of animals that are ready to move.
     * @returns {Array<object>} A list of animal data objects.
     * @private
     */
    _getAnimalsReadyToMove() {
        const animalsReadyToMove = [];
        for (const [id, pos] of this.animalsOnGrid.entries()) {
            const item = this.grid.get(pos.r, pos.c);
            if (item && item.turnsUntilMove !== undefined && item.turnsUntilMove <= 0) {
                animalsReadyToMove.push({ r: pos.r, c: pos.c, item });
            }
        }

        // Shuffle to make movement order random
        for (let i = animalsReadyToMove.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [animalsReadyToMove[i], animalsReadyToMove[j]] = [animalsReadyToMove[j], animalsReadyToMove[i]];
        }

        return animalsReadyToMove;
    }

    /**
     * Calculates the moves for a given list of animals.
     * @param {Array<object>} animalsReadyToMove - A list of animals to move.
     * @returns {Array<object>} A list of move objects.
     * @private
     */
    _calculateMoves(animalsReadyToMove) {
        const movesToAnimate = [];
        const occupiedDestinations = new Set();

        animalsReadyToMove.forEach(animalData => {
            const { r, c, item } = animalData;
            const emptyNeighbors = this.grid.findEmptyNeighbors(r, c)
                .filter(neighbor => !occupiedDestinations.has(`${neighbor.r},${neighbor.c}`));

            if (emptyNeighbors.length > 0) {
                const newPos = emptyNeighbors[Math.floor(Math.random() * emptyNeighbors.length)];
                occupiedDestinations.add(`${newPos.r},${newPos.c}`);
                movesToAnimate.push({ from: { r, c }, to: newPos, item });
            }
        });

        return movesToAnimate;
    }

    /**
     * Applies the calculated moves to the grid.
     * @param {Array<object>} movesToAnimate - A list of move objects.
     * @private
     */
    _applyMoves(movesToAnimate) {
        // First, remove all animals from their old positions
        movesToAnimate.forEach(move => {
            const animalConfig = this.gameConfig.animals.find(a => a.key === move.item.key);
            if (animalConfig) {
                move.item.turnsUntilMove = animalConfig.moveCooldownInTicks;
            }
            this.grid.set(move.from.r, move.from.c, null);
            this.animalsOnGrid.set(move.item.id, { r: move.to.r, c: move.to.c });
        });

        // Then, place them in their new positions
        movesToAnimate.forEach(move => {
            this.grid.set(move.to.r, move.to.c, move.item);
        });
    }

    /**
     * Gets the current state of the AnimalManager for saving.
     * @returns {object} The state object containing turnsUntilSpawn, activeAnimal, nextAnimalId, and animalsOnGrid.
     */
    getState() {
        return {
            turnsUntilSpawn: this.turnsUntilSpawn,
            activeAnimal: this.activeAnimal ? { k: this.activeAnimal.key } : null,
            nextAnimalId: this.nextAnimalId,
            animalsOnGrid: Array.from(this.animalsOnGrid.entries()), // Convert Map to Array for JSON serialization
        };
    }

    /**
     * Restores the state of the AnimalManager from a saved state.
     * @param {object} state - The saved state object.
     * @param {number} state.turnsUntilSpawn - The number of turns until the next animal spawn.
     * @param {object|null} state.activeAnimal - The active animal configuration, or null.
     * @param {number} state.nextAnimalId - The next available animal ID.
     * @param {Array<[number, {r: number, c: number}]>} state.animalsOnGrid - An array representation of the animals on the grid map.
     */
    restoreState(state) {
        if (!state) return;
        this.turnsUntilSpawn = state.turnsUntilSpawn ?? this.gameConfig.turnsPerAnimal;
        this.activeAnimal = state.activeAnimal ? this.gameConfig.animals.find(a => a.key === state.activeAnimal.k) : null;
        this.nextAnimalId = state.nextAnimalId || 0;
        this.animalsOnGrid = new Map(state.animalsOnGrid || []);
    }

    getTurnsUntilNextSpawn() {
        return this.turnsUntilSpawn; // Corrected to use the existing property
    }

    /**
     * Gets the configuration object for the next animal that will spawn.
     * @returns {object|null} The next animal's config object, or null if none are available.
     */
    getNextAnimalToSpawn() {
        // The activeAnimal is the one that will be spawned next.
        return this.activeAnimal || null;
    }
}